import { type TypedDocumentNode, gql } from '@apollo/client';
import { faCircleCheck, faEdit } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { FirstLineupPreview_card } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0 })`
  border-radius: var(--double-unit);
  background: linear-gradient(262deg, #f1ff98 5.3%, #87f6ab 91.08%), transparent;
`;
const FirstLineupPreviewHeader = styled(Horizontal)`
  padding: var(--unit);
`;
const FirstLineupPreviewContent = styled(Vertical).attrs({ gap: 0 })`
  border-radius: calc(var(--double-unit) - 1px);
  background:
    no-repeat center / cover var(--bg-url),
    var(--c-black);
  padding: var(--double-unit) var(--double-unit) 0 var(--double-unit);
`;
const RightAlignedHorizontal = styled(Horizontal)`
  justify-content: flex-end;
`;
const Footer = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit) 0;
`;

type Props = {
  cards: FirstLineupPreview_card[];
  backgroundUrl?: string | null;
};
export const FirstLineupPreview = ({ cards, backgroundUrl }: Props) => {
  return (
    <Root>
      <FirstLineupPreviewHeader center>
        <FontAwesomeIcon icon={faCircleCheck} color="var(--c-black)" />
        <BrandS color="var(--c-black)">
          <FormattedMessage
            id="RecommendedPlayers.discountApplied"
            defaultMessage="Discount applied"
          />
        </BrandS>
      </FirstLineupPreviewHeader>
      <FirstLineupPreviewContent
        style={{ '--bg-url': `url(${backgroundUrl || ''})` } as CSSProperties}
      >
        <RightAlignedHorizontal>
          <Button
            color="secondary"
            size="medium"
            startIcon={<FontAwesomeIcon icon={faEdit} />}
          >
            <FormattedMessage {...glossary.edit} />
          </Button>
        </RightAlignedHorizontal>
        <LineupCardsLayout fillHeightPercent={0.6}>
          {cards.map(card => (
            <Vertical key={card.slug} center>
              <Card card={card} />
            </Vertical>
          ))}
        </LineupCardsLayout>
        <Footer>
          <LabelM>
            <FormattedMessage
              id="RecommendedPlayers.tapPlayerForInfo"
              defaultMessage="Tap a player to learn more"
            />
          </LabelM>
        </Footer>
      </FirstLineupPreviewContent>
    </Root>
  );
};

FirstLineupPreview.fragments = {
  card: gql`
    fragment FirstLineupPreview_card on Card {
      slug
      id
      pictureUrl
      ...Card_anyCard
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<FirstLineupPreview_card>,
};
